import { prefersReducedMotion } from './prefersReducedMotion';

let isBatteryOn = false;
if (typeof navigator !== 'undefined' && navigator.getBattery) {
  navigator.getBattery().then(battery => {
    isBatteryOn = !battery.charging;
    battery.addEventListener('chargingchange', () => {
      isBatteryOn = !battery.charging;
    });
  }).catch(() => {});
}

export async function isWebGPUSupported() {
  if (typeof navigator === 'undefined' || !navigator.gpu) return false;
  try {
    const adapter = await navigator.gpu.requestAdapter();
    return !!adapter;
  } catch (e) {
    return false;
  }
}

export class WebGPUShaderEngine {
  constructor(canvas) {
    this.canvas = canvas;
    this.device = null;
    this.context = null;
    this.pipeline = null;
    this.animationFrameId = null;
    this.mode = 'webgpu'; // 'webgpu' | 'webgl2' | 'fallback'
    this.lastFrameTime = 0;
    this.consecutiveSlowFrames = 0;
    this.targetFPS = 60;
  }

  async init() {
    if (prefersReducedMotion()) {
      this.mode = 'fallback';
      return false;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.gpu) {
        const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'low-power' });
        if (adapter) {
          this.device = await adapter.requestDevice();
          this.context = this.canvas.getContext('webgpu');
          if (this.context && this.device) {
            const format = navigator.gpu.getPreferredCanvasFormat();
            this.context.configure({
              device: this.device,
              format: format,
              alphaMode: 'premultiplied'
            });
            this.mode = 'webgpu';
            console.log('[SacredMothr] WebGPU Shader Engine initialized successfully.');
            return true;
          }
        }
      }
    } catch (err) {
      console.warn('[SacredMothr] WebGPU init failed, falling back to WebGL2:', err);
    }

    this.mode = 'webgl2';
    return false;
  }

  start(renderCallback) {
    let lastRenderTimestamp = performance.now();

    const loop = (timestamp) => {
      if (document.hidden) {
        this.animationFrameId = requestAnimationFrame(loop);
        return;
      }

      this.targetFPS = isBatteryOn ? 30 : 60;
      const interval = 1000 / this.targetFPS;
      const delta = timestamp - lastRenderTimestamp;

      if (delta >= interval) {
        const renderStart = performance.now();
        
        try {
          renderCallback({
            mode: this.mode,
            device: this.device,
            context: this.context,
            timestamp
          });
        } catch (e) {
          console.warn('[SacredMothr] Render loop error, switching mode:', e);
          this.mode = 'fallback';
        }

        const renderDuration = performance.now() - renderStart;

        // Auto-downgrade performance check: if frame render time > 16ms over 15 consecutive frames
        if (renderDuration > 16) {
          this.consecutiveSlowFrames += 1;
          if (this.consecutiveSlowFrames > 15) {
            console.warn('[SacredMothr] High GPU load detected (>16ms per frame). Downgrading shader mode.');
            if (this.mode === 'webgpu') this.mode = 'webgl2';
            else if (this.mode === 'webgl2') this.mode = 'fallback';
            this.consecutiveSlowFrames = 0;
          }
        } else {
          this.consecutiveSlowFrames = Math.max(0, this.consecutiveSlowFrames - 1);
        }

        lastRenderTimestamp = timestamp - (delta % interval);
      }

      this.animationFrameId = requestAnimationFrame(loop);
    };

    this.animationFrameId = requestAnimationFrame(loop);
  }

  stop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}
